import React from 'react'

import { Heading } from '@hhgtech/hhg-components'
import { Popover, Text } from '@hhgtech/hhg-components/mantine'
import { ArrowRight } from '@hhgtech/icons/other'
import { Box, Button, Flex, rem } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dynamic from 'next/dynamic'
import { useIntl } from 'react-intl'

import { Disclaimer } from 'components/atoms/disclaimer'
import ExternalUrl from 'components/atoms/icons/ExternalUrl'
import { useIsMobile } from 'hooks/useIsMobile'

import useStyles from './index.styles'
import { BlockItemPropsType } from '..'

const RectPattern = dynamic(() => import('./rectPattern'))
const SquarePattern = dynamic(() => import('./squarePattern'))

export type PatternType = {
  color: string
  size?: number
}

const FullBg = ({
  image,
  isImageBg,
  title,
  description,
  ctaTitle,
  bgColor = 'blue.6',
  className,
  classNames,
  isQuare = true,
  showBrushPattern,
  styles,
  isExternalUrl,
  hideDescription,
  useImgAsFullBg = false,
  ...boxProps
}: BlockItemPropsType) => {
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()
  const { classes, cx } = useStyles(
    {
      bgColor,
      isQuare,
      useImgAsFullBg,
      image,
      isImageBg,
    },
    {
      name: 'CarePathHeroBannerNavigatorItem____BlockItem--FullBg',
      classNames,
      styles,
    },
  )
  const [opened, { close, open }] = useDisclosure(false)

  const hintText = (
    <Flex gap={rem(4)} align={'center'}>
      <ExternalUrl size={18} color="#fff" />
      <Text size="n1" color="#fff">
        {f({
          id: 'ctaBanner.leavingSite',
          defaultMessage: 'Leaving site',
        })}
      </Text>
    </Flex>
  )

  return (
    <Box
      className={cx(classes.root, className)}
      {...boxProps}
      onMouseEnter={open}
      onMouseLeave={close}
    >
      {!isImageBg && (
        <>
          <Box className={classes.wrapper}>
            <Box className={classes.info}>
              {title && (
                <Heading className={classes.title} tag={isMobile ? 'h4' : 'h3'}>
                  {title}
                </Heading>
              )}
              {!isMobile && description && !hideDescription && (
                <Text size="p4" className={classes.desc}>
                  {description}
                </Text>
              )}
              <Box className={classes.exploreLink}>
                <Text className={classes.exploreText} size="s4">
                  {ctaTitle ||
                    f({
                      id: 'ctaBanner.exploreLink',
                      defaultMessage: 'Explore',
                    })}
                </Text>
                <span className={classes.exploreArrow}>
                  <ArrowRight size={18} useCurrentColor />
                </span>
              </Box>
            </Box>
          </Box>
          <Box className={classes.pattern}>
            {isQuare ? (
              <SquarePattern
                color={bgColor}
                showBrush={showBrushPattern && !isMobile}
              />
            ) : (
              <RectPattern
                color={bgColor}
                showBrush={showBrushPattern && !isMobile}
              />
            )}
          </Box>
          {image && (
            <img
              src={image}
              loading="eager"
              alt={title}
              className={classes.thumbnail}
            />
          )}
        </>
      )}

      {isExternalUrl ? (
        isMobile ? (
          <Box className={classes.hint}>{hintText}</Box>
        ) : (
          <Box className={classes.hint}>
            <Popover
              width={264}
              withinPortal
              position="bottom"
              withArrow
              shadow="md"
              opened={opened}
              classNames={{
                dropdown: classes.popoperDropdown,
                arrow: classes.popoperArrow,
              }}
            >
              <Popover.Target>
                <Button
                  sx={{
                    pointerEvents: 'auto',
                    height: 'auto',
                    padding: 0,
                  }}
                >
                  {hintText}
                </Button>
              </Popover.Target>
              <Popover.Dropdown>
                <Disclaimer
                  classNames={{
                    root: classes.disclaimer,
                    content: classes.disclaimerText,
                  }}
                  content={f({
                    id: 'confirmLeavePage.content',
                    defaultMessage:
                      'As your trusted health partner, we want to ensure you have the information you need to safeguard your health. Are you sure you want to leave this page?',
                  })}
                />
              </Popover.Dropdown>
            </Popover>
          </Box>
        )
      ) : null}
    </Box>
  )
}

FullBg.displayName = 'FullBg'

export { FullBg }
